import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { currencyForCompany } from "lib/companyProfile";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import MxShell from "../components/MxShell";
import { formatMoney, mx } from "../lib/theme";

function QtyStepper({ qty, onDec, onInc }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        border: `1px solid ${mx.hairline}`,
        borderRadius: "10px",
        bgcolor: mx.canvas,
        px: 0.25,
      }}
    >
      <ButtonBase
        onClick={onDec}
        aria-label="−"
        sx={{ width: 36, height: 36, color: mx.ink, borderRadius: "8px" }}
      >
        <Icon sx={{ fontSize: 18 }}>remove</Icon>
      </ButtonBase>
      <Typography sx={{ width: 28, textAlign: "center", fontWeight: 800, color: mx.ink, fontSize: 15 }}>
        {qty}
      </Typography>
      <ButtonBase
        onClick={onInc}
        aria-label="+"
        sx={{ width: 36, height: 36, color: mx.ink, borderRadius: "8px" }}
      >
        <Icon sx={{ fontSize: 18 }}>add</Icon>
      </ButtonBase>
    </Stack>
  );
}

QtyStepper.propTypes = {
  qty: PropTypes.number.isRequired,
  onDec: PropTypes.func.isRequired,
  onInc: PropTypes.func.isRequired,
};

export default function CartPage() {
  const navigate = useNavigate();
  const { token, storefrontTenant } = useAuth();
  const { lines, removeItem, setQty, subtotal, itemSubtotal, coreSubtotal } = useCart();
  const { t } = useStorefrontCopy();
  const currency = currencyForCompany(storefrontTenant || "mx");
  const hasItems = (lines || []).length > 0;

  if (!token) {
    return (
      <MxShell title={t("cart.title", "Carrito")} onBack={() => navigate("/m/catalog")}>
        <Box
          sx={{
            mt: 2,
            p: 2.5,
            borderRadius: "14px",
            bgcolor: mx.surface,
            border: `1px solid ${mx.hairline}`,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 1.5,
              borderRadius: "14px",
              bgcolor: mx.accentSoft,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon sx={{ color: mx.accent, fontSize: 28 }}>lock</Icon>
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 750, color: mx.ink, mb: 0.75 }}>
            {t("common.signIn", "Iniciar sesión")}
          </Typography>
          <Typography sx={{ fontSize: 14, color: mx.muted, lineHeight: 1.5, mb: 2 }}>
            {t("cart.signInRequired", "El pago requiere iniciar sesión con una cuenta de cliente.")}
          </Typography>
          <ButtonBase
            onClick={() => navigate("/m/authentication/sign-in")}
            sx={{
              width: "100%",
              bgcolor: mx.accent,
              color: "#fff",
              py: 1.4,
              borderRadius: "10px",
              fontSize: 15,
              fontWeight: 800,
              boxShadow: "0 10px 24px rgba(255,92,0,0.28)",
              "&:hover": { bgcolor: mx.accentHover },
            }}
          >
            {t("common.signIn", "Iniciar sesión")}
          </ButtonBase>
        </Box>
      </MxShell>
    );
  }

  const footer = hasItems ? (
    <Stack spacing={1.1}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: mx.muted }}>
          {t("cart.total", "Total del carrito")}
        </Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: mx.ink, letterSpacing: -0.3 }}>
          {formatMoney(subtotal, currency)}
        </Typography>
      </Stack>
      <ButtonBase
        onClick={() => navigate("/m/checkout")}
        sx={{
          width: "100%",
          bgcolor: mx.accent,
          color: "#fff",
          py: 1.45,
          px: 2,
          borderRadius: "10px",
          fontSize: 15,
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          boxShadow: "0 10px 24px rgba(255,92,0,0.28)",
          "&:hover": { bgcolor: mx.accentHover },
        }}
      >
        <Icon sx={{ fontSize: 20 }}>shopping_bag</Icon>
        <span>{t("cart.checkout", "Pagar")}</span>
        <Icon sx={{ fontSize: 20 }}>arrow_forward</Icon>
      </ButtonBase>
    </Stack>
  ) : null;

  return (
    <MxShell title={t("cart.title", "Carrito")} onBack={() => navigate("/m/catalog")} footer={footer}>
      {!hasItems ? (
        <Box
          sx={{
            mt: 2,
            p: 2.75,
            borderRadius: "14px",
            bgcolor: mx.surface,
            border: `1px solid ${mx.hairline}`,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 1.5,
              borderRadius: "14px",
              bgcolor: mx.accentSoft,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon sx={{ color: mx.accent, fontSize: 28 }}>shopping_cart</Icon>
          </Box>
          <Typography sx={{ fontSize: 17, fontWeight: 750, color: mx.ink, mb: 0.75 }}>
            {t("cart.emptyTitle", "Su carrito está vacío")}
          </Typography>
          <Typography sx={{ fontSize: 14, color: mx.muted, lineHeight: 1.5, mb: 2.25 }}>
            {t("cart.emptyText", "Agregue productos del catálogo para iniciar un pedido.")}
          </Typography>
          <ButtonBase
            onClick={() => navigate("/m/catalog")}
            sx={{
              width: "100%",
              bgcolor: mx.accent,
              color: "#fff",
              py: 1.4,
              borderRadius: "10px",
              fontSize: 15,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              boxShadow: "0 10px 24px rgba(255,92,0,0.28)",
              "&:hover": { bgcolor: mx.accentHover },
            }}
          >
            <Icon sx={{ fontSize: 20 }}>storefront</Icon>
            <span>{t("common.browseCatalog", "Ver catálogo")}</span>
          </ButtonBase>
        </Box>
      ) : (
        <Stack spacing={1.35} sx={{ mt: 1.25 }}>
          {(lines || []).map((line) => {
            const qty = Number(line.qty || 1) || 1;
            const unit = Number(line.unitPrice || 0) || 0;
            const core = Number(line.coreCharge || 0) || 0;
            const lineTotal = qty * (unit + core);

            return (
              <Box
                key={line.documentId}
                sx={{
                  p: 1.5,
                  borderRadius: "14px",
                  bgcolor: mx.surface,
                  border: `1px solid ${mx.hairline}`,
                  boxShadow: "0 8px 22px rgba(15,15,18,0.04)",
                }}
              >
                <Stack direction="row" spacing={1.35} alignItems="flex-start">
                  {line.imageUrl ? (
                    <Box
                      component="img"
                      src={line.imageUrl}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: 84,
                        height: 84,
                        objectFit: "cover",
                        borderRadius: "12px",
                        bgcolor: mx.canvas,
                        flexShrink: 0,
                        display: "block",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 84,
                        height: 84,
                        borderRadius: "12px",
                        bgcolor: mx.canvas,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        border: `1px solid ${mx.hairline}`,
                      }}
                    >
                      <Icon sx={{ color: mx.subtle, fontSize: 28 }}>inventory_2</Icon>
                    </Box>
                  )}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.75}>
                      <Box sx={{ minWidth: 0 }}>
                        {line.sku ? (
                          <Typography
                            sx={{
                              fontFamily: mx.fontMono,
                              fontSize: 10.5,
                              fontWeight: 750,
                              color: mx.accent,
                              letterSpacing: 0.7,
                              textTransform: "uppercase",
                            }}
                          >
                            {line.sku}
                          </Typography>
                        ) : null}
                        <Typography
                          sx={{
                            mt: 0.2,
                            fontSize: 15.5,
                            fontWeight: 750,
                            lineHeight: 1.28,
                            color: mx.ink,
                          }}
                        >
                          {line.name || line.sku || "—"}
                        </Typography>
                        <Typography sx={{ mt: 0.45, fontSize: 14.5, fontWeight: 750, color: mx.ink }}>
                          {formatMoney(unit, currency)}
                          {core > 0 ? (
                            <Typography component="span" sx={{ ml: 0.5, fontSize: 13, fontWeight: 600, color: mx.muted }}>
                              + {formatMoney(core, currency)} core
                            </Typography>
                          ) : null}
                        </Typography>
                      </Box>
                      <ButtonBase
                        onClick={() => removeItem(line.documentId)}
                        aria-label={t("common.remove", "Eliminar")}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "10px",
                          color: mx.red,
                          bgcolor: "rgba(215,38,30,0.07)",
                          flexShrink: 0,
                          "&:active": { opacity: 0.7 },
                        }}
                      >
                        <Icon sx={{ fontSize: 18 }}>delete_outline</Icon>
                      </ButtonBase>
                    </Stack>

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mt: 1.25 }}
                    >
                      <QtyStepper
                        qty={qty}
                        onDec={() => setQty(line.documentId, Math.max(1, qty - 1))}
                        onInc={() => setQty(line.documentId, Math.min(99, qty + 1))}
                      />
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: mx.ink, letterSpacing: -0.2 }}>
                        {formatMoney(lineTotal, currency)}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            );
          })}

          <Box
            sx={{
              p: 2,
              borderRadius: "14px",
              bgcolor: mx.surface,
              border: `1px solid ${mx.hairline}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 800,
                letterSpacing: 1,
                textTransform: "uppercase",
                color: mx.subtle,
                mb: 1.25,
              }}
            >
              {t("cart.summary", "Resumen")}
            </Typography>
            <Stack spacing={0.9}>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ color: mx.muted, fontSize: 14.5 }}>Subtotal</Typography>
                <Typography sx={{ fontWeight: 750, fontSize: 14.5 }}>{formatMoney(itemSubtotal, currency)}</Typography>
              </Stack>
              {coreSubtotal > 0 ? (
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ color: mx.muted, fontSize: 14.5 }}>Core</Typography>
                  <Typography sx={{ fontWeight: 750, fontSize: 14.5 }}>{formatMoney(coreSubtotal, currency)}</Typography>
                </Stack>
              ) : null}
              <Box sx={{ height: 1, bgcolor: mx.hairline, my: 0.35 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography sx={{ fontWeight: 750, fontSize: 15 }}>{t("cart.total", "Total del carrito")}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 18, color: mx.accent, letterSpacing: -0.25 }}>
                  {formatMoney(subtotal, currency)}
                </Typography>
              </Stack>
            </Stack>
            {coreSubtotal > 0 ? (
              <Typography sx={{ mt: 1.25, fontSize: 12.5, color: mx.muted, lineHeight: 1.45 }}>
                {t("cart.refundableDeposit", "El depósito es reembolsable después de la devolución y aprobación.")}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      )}
    </MxShell>
  );
}
